import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GlassSpinner from "../../components/GlassSpinner";
import { useEmpresa } from "../../context/EmpresaContext";
import { getEntradas, getEventos, uploadLogo } from "../../services/sininApi";
import { useToast, ToastPopup } from "../../components/Toast";
import sininLogo from "../../assets/sinin-logo.svg";

const API  = "https://mocaub1h8d.execute-api.us-east-1.amazonaws.com/prod";
const SININ_TURQ = "#25f4df";
const companyLogoSrc = item => item?.logoUrl || (item?.logoKey
  ? `https://buckettree.s3.amazonaws.com/${item.logoKey}`
  : sininLogo);

const FORM_EMPTY = {
  empresaId:    "",
  nombre:       "",
  adminDni:     "",
  adminPass:    "",
  smtpUser:     "",
  smtpPass:     "",
  colorTicket:  "#dfa66b",
  colorFondo:   "#3a3a3a",
  colorFuente:  "#ffffff",
  colorDark:    "#333333",
  colorLight:   "#555555",
  logoKey:      "",
  pdfPrefix:    "",
  mailAsunto:   "Tus entradas",
  mailCuerpo:   "Presentá tu entrada con DNI en el ingreso.",
  mailEnabled:  false,
  scannerPass:  "",
  vendedorTitulo: "Panel de vendedor",
  vendedorAccion: "Generar ticket",
  ticketNombre: "Ticket",
  ticketNombrePlural: "Tickets",
  mpEnabled:    false,
  mpPublicKey:  "",
  mpAccessToken: "",
  mpWebhookSecret: "",
  mpMode:       "test",
  mpFeePercent: "0",
  wsPhone:      "",
};

export default function AdminPanel() {
  const navigate           = useNavigate();
  const { empresa, setEmpresa, logout } = useEmpresa();

  const [tab, setTab]           = useState("empresas");
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm]         = useState(FORM_EMPTY);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [globalStats, setGlobalStats] = useState(null);
  const { toast, showToast, closeToast } = useToast();

  useEffect(() => {
    if (empresa?.role !== "superadmin") {
      navigate("/ADMIN");
      return;
    }
    cargar();
  }, []); // eslint-disable-line

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/sinin_gestionarEmpresas`, { action: "list" });
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      const empresasConStats = await Promise.all(items.map(async item => {
        try {
          const [eventos, entradasRes] = await Promise.all([
            getEventos(item.empresaId, "1"),
            getEntradas(item.empresaId),
          ]);
          const entradas = Array.isArray(entradasRes?.items) ? entradasRes.items : [];
          return {
            ...item,
            _stats: {
              eventos: Array.isArray(eventos) ? eventos.length : 0,
              tickets: entradas.length,
              ingresaron: entradas.filter(ticket => Boolean(ticket.used)).length,
              pendientes: entradas.filter(ticket => !ticket.used).length,
            },
          };
        } catch {
          return { ...item, _stats: { eventos: 0, tickets: 0, ingresaron: 0, pendientes: 0 } };
        }
      }));
      setEmpresas(empresasConStats);
      setGlobalStats(empresasConStats.reduce((acc, item) => ({
        empresas: acc.empresas + 1,
        eventos: acc.eventos + item._stats.eventos,
        tickets: acc.tickets + item._stats.tickets,
        ingresaron: acc.ingresaron + item._stats.ingresaron,
        pendientes: acc.pendientes + item._stats.pendientes,
      }), { empresas: 0, eventos: 0, tickets: 0, ingresaron: 0, pendientes: 0 }));
    } catch {}
    setLoading(false);
  };

  const setF = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleLogoFile = async (file) => {
    if (!file) return;
    const empresaIdSlug = (form.empresaId || form.nombre).toLowerCase().replace(/\s+/g, "_");
    if (!empresaIdSlug) return showToast("Completá primero el empresaId o nombre.", "error");
    setUploadingLogo(true);
    try {
      const fileB64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await uploadLogo(empresaIdSlug, fileB64, file.name);
      if (res.logoKey) setForm(p => ({ ...p, logoKey: res.logoKey }));
      else showToast("Error al subir el logo: " + (res.error || ""), "error");
    } catch {
      showToast("Error al subir el logo.", "error");
    }
    setUploadingLogo(false);
  };

  const abrirNueva = () => {
    setForm(FORM_EMPTY);
    setEditItem(null);
    setShowForm(true);
    setTab("form");
  };

  const abrirEditar = (emp) => {
    setForm({
      empresaId:    emp.empresaId,
      nombre:       emp.nombre       || "",
      adminDni:     "",
      adminPass:    "",
      smtpUser:     emp.smtpUser     || emp.smtpUserBackup || "",
      smtpPass:     emp.smtpPass     || "",
      colorTicket:  emp.colorTicket  || "#dfa66b",
      colorFondo:   emp.colorFondo   || "#3a3a3a",
      colorFuente:  emp.colorFuente  || "#ffffff",
      colorDark:    emp.colorDark    || "#333333",
      colorLight:   emp.colorLight   || "#555555",
      logoKey:      emp.logoKey      || "",
      pdfPrefix:    emp.pdfPrefix    || "",
      mailAsunto:   emp.mailAsunto   || "Tus entradas",
      mailCuerpo:   emp.mailCuerpo   || "",
      mailEnabled:  emp.mailEnabled === true,
      scannerPass:  emp.scannerPass  || "",
      vendedorTitulo: emp.vendedorTitulo || "Panel de vendedor",
      vendedorAccion: emp.vendedorAccion || "Generar ticket",
      ticketNombre: emp.ticketNombre || "Ticket",
      ticketNombrePlural: emp.ticketNombrePlural || "Tickets",
      mpEnabled:      emp.mpEnabled || false,
      mpPublicKey:    emp.mpPublicKey || "",
      mpAccessToken:  emp.mpAccessToken || "",
      mpWebhookSecret: emp.mpWebhookSecret || "",
      mpMode:         emp.mpMode || "test",
      mpFeePercent:   emp.mpFeePercent || "0",
      wsPhone:        emp.wsPhone || "",
    });
    setEditItem(emp);
    setShowForm(true);
    setTab("form");
  };

  const handleGuardar = async () => {
    const id = (form.empresaId || form.nombre).toLowerCase().replace(/\s+/g, "_");
    if (!id || !form.nombre) return showToast("empresaId y nombre requeridos.", "error");
    setSaving(true);
    try {
      await axios.post(`${API}/sinin_gestionarEmpresas`, {
        action: "upsert",
        empresa: {
          ...form,
          empresaId: id,
          // Compatibilidad con Lambdas anteriores: sin remitente activo no
          // pueden enviar aunque todavía no conozcan mailEnabled.
          smtpUser: form.mailEnabled ? form.smtpUser : "",
          smtpUserBackup: form.smtpUser || editItem?.smtpUserBackup || editItem?.smtpUser || "",
          pdfPrefix: form.pdfPrefix || `${id}/pdfs/`,
          logoKey:   form.logoKey   || `${id}/logo.png`,
          active:    "1",
        },
        adminDni:  form.adminDni,
        adminPass: form.adminPass,
      });
      showToast(editItem ? "Empresa actualizada." : "Empresa creada correctamente.", "success");
      setShowForm(false);
      setTab("empresas");
      cargar();
    } catch { showToast("Error al guardar.", "error"); }
    setSaving(false);
  };

  const handleLogout = () => { logout(); navigate("/ADMIN"); };

  const entrarComoEmpresa = (emp) => {
    localStorage.setItem("sinin_admin_session", JSON.stringify(empresa));
    setEmpresa({
      empresaId:   emp.empresaId,
      role:        "users",
      loginType:   "2",
      nombre:      emp.nombre,
      first_name:  "Super",
      last_name:   "Admin",
      dni:         empresa.dni,
      colorTicket: emp.colorTicket,
      colorFondo:  emp.colorFondo,
      colorFuente: emp.colorFuente,
      _impersonating: true,
    });
    navigate("/cliente/eventos");
  };

  return (
    <div style={styles.page}>
      {saving && (
        <div style={styles.savingOverlay}>
          <GlassSpinner size={82} background="#071416" />
          <div style={styles.savingTitle}>
            {editItem ? "GUARDANDO EMPRESA..." : "CREANDO EMPRESA..."}
          </div>
          <div style={styles.savingText}>
            {editItem ? "Actualizando la configuración" : "Preparando tablas, carpetas y usuario administrador"}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={sininLogo} alt="SININ" style={styles.headerLogo} />
          <div>
            <div style={{ fontWeight: "900", fontSize: 14, letterSpacing: 2 }}>SUPER ADMIN</div>
            <div style={{ fontSize: 10, opacity: 0.65, letterSpacing: 1 }}>GESTIÓN GENERAL</div>
          </div>
        </div>
        <button style={styles.outlineBtn} onClick={handleLogout}>SALIR</button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {[["empresas","Empresas"], ["form", showForm ? (editItem ? "Editar empresa" : "Nueva empresa") : "+ Nueva empresa"]].map(([t, l]) => (
          <button key={t} onClick={() => { if (t === "form") abrirNueva(); else setTab(t); }}
            style={adminTabStyle(tab === t)}>
            {l}
          </button>
        ))}
      </div>

      <div style={styles.content}>

        {/* ── FORM ── */}
        {tab === "form" && showForm && (
          <div style={styles.card}>
            <div style={styles.companyTitleRow}>
              <img
                src={editItem ? companyLogoSrc(editItem) : sininLogo}
                alt=""
                style={styles.companyLogo}
                onError={e => { e.currentTarget.src = sininLogo; }}
              />
              <h3 style={{ margin: 0 }}>{editItem ? `Editar: ${editItem.nombre}` : "Nueva empresa"}</h3>
            </div>

            <Section title="Identificación">
              <Row label="empresaId (slug, sin espacios) *">
                <input style={styles.input} value={form.empresaId} onChange={setF("empresaId")}
                  placeholder="sensoria" disabled={!!editItem} />
              </Row>
              <Row label="Nombre visible *">
                <input style={styles.input} value={form.nombre} onChange={setF("nombre")} placeholder="Sensoria" />
              </Row>
            </Section>

            <Section title={editItem ? "Resetear / crear admin de la empresa" : "Admin de la empresa (primer usuario)"}>
              {editItem && (
                <div style={{ fontSize: 12, color: "rgba(239,255,253,.65)", marginBottom: 4 }}>
                  Dejá estos campos vacíos para no tocar los admins existentes. Completalos para crear uno nuevo o resetear su contraseña.
                </div>
              )}
              <Row label="DNI del admin">
                <input style={styles.input} value={form.adminDni} onChange={setF("adminDni")} placeholder="12345678" />
              </Row>
              <Row label="Contraseña">
                <input style={styles.input} value={form.adminPass} onChange={setF("adminPass")} placeholder="contraseña" />
              </Row>
            </Section>

            <Section title="Email para envío de tickets">
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: "700", fontSize: 13, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.mailEnabled}
                  onChange={e => setForm(p => ({ ...p, mailEnabled: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                Enviar tickets por mail
              </label>
              {form.mailEnabled && (
                <>
                  <Row label="Gmail">
                    <input style={styles.input} value={form.smtpUser} onChange={setF("smtpUser")} placeholder="hola@gmail.com" />
                  </Row>
                  <Row label="App Password de Gmail">
                    <input style={styles.input} value={form.smtpPass} onChange={setF("smtpPass")}
                      placeholder={editItem ? "Vacío = conservar la actual" : "xxxx xxxx xxxx xxxx"} />
                  </Row>
                  <Row label="Asunto del mail">
                    <input style={styles.input} value={form.mailAsunto} onChange={setF("mailAsunto")} />
                  </Row>
                  <Row label="Cuerpo del mail">
                    <input style={styles.input} value={form.mailCuerpo} onChange={setF("mailCuerpo")} />
                  </Row>
                </>
              )}
            </Section>

            <Section title="Diseño del ticket y la app">
              {[
                ["colorTicket", "Color principal (ticket, botones)"],
                ["colorFondo",  "Color de fondo"],
                ["colorFuente", "Color de tipografía"],
              ].map(([key, label]) => (
                <Row key={key} label={label}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input style={{ ...styles.input, flex: 1 }} value={form[key]} onChange={setF(key)} />
                    <input type="color" value={form[key]} onChange={setF(key)}
                      style={{ width: 36, height: 36, border: "none", cursor: "pointer", borderRadius: 6 }} />
                  </div>
                </Row>
              ))}
              <Row label="Logo">
                <input type="file" accept="image/*"
                  disabled={uploadingLogo}
                  onChange={e => handleLogoFile(e.target.files?.[0])}
                  style={{ fontSize: 13, color: "#dffffb" }} />
                {uploadingLogo && <div style={{ fontSize: 12, color: SININ_TURQ, marginTop: 6 }}>SUBIENDO LOGO...</div>}
                {form.logoKey && (
                  <img
                    src={`https://buckettree.s3.amazonaws.com/${form.logoKey}?t=${Date.now()}`}
                    alt="logo preview"
                    style={{ marginTop: 8, maxHeight: 70, maxWidth: "100%", objectFit: "contain", background: "transparent" }}
                    onError={e => e.target.style.display = "none"}
                  />
                )}
              </Row>
              <Row label="S3 prefix PDFs (ej: sensoria/pdfs/)">
                <input style={styles.input} value={form.pdfPrefix} onChange={setF("pdfPrefix")} placeholder="sensoria/pdfs/" />
              </Row>
            </Section>

            <Section title="Textos del panel de vendedores">
              <Row label="Clave scanner / puerta">
                <input
                  style={styles.input}
                  value={form.scannerPass}
                  onChange={setF("scannerPass")}
                  placeholder="Clave para personal de ingreso"
                />
                <small style={{ color: "#999" }}>
                  Esta clave permite entrar solo al scanner, sin ver invitados ni estadísticas.
                </small>
              </Row>
              <Row label="Título del panel">
                <input style={styles.input} value={form.vendedorTitulo} onChange={setF("vendedorTitulo")} />
              </Row>
              <Row label="Acción principal">
                <input style={styles.input} value={form.vendedorAccion} onChange={setF("vendedorAccion")} />
              </Row>
              <Row label="Nombre singular de la entrada">
                <input style={styles.input} value={form.ticketNombre} onChange={setF("ticketNombre")} />
              </Row>
              <Row label="Nombre plural de las entradas">
                <input style={styles.input} value={form.ticketNombrePlural} onChange={setF("ticketNombrePlural")} />
              </Row>
              <Row label="Teléfono de WhatsApp (con código de país, sin +)">
                <input style={styles.input} value={form.wsPhone} onChange={setF("wsPhone")}
                  placeholder="5491112345678" />
                <small style={{ color: "#999" }}>
                  Se usa en el link de lista de espera cuando el QR aún no fue aprobado.
                </small>
              </Row>
            </Section>

            <Section title="Mercado Pago (cobro de entradas pagas)">
              <div style={{ fontSize: 12, color: "rgba(239,255,253,.65)", marginBottom: 8, lineHeight: 1.5 }}>
                Permite que esta empresa cobre las entradas pagas directamente con Mercado Pago.
                Las credenciales se obtienen desde el panel de desarrolladores de Mercado Pago de la cuenta de la empresa.
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
                <input type="checkbox" id="mpEnabled" checked={form.mpEnabled}
                  onChange={e => setForm(p => ({ ...p, mpEnabled: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: "pointer" }} />
                <label htmlFor="mpEnabled" style={{ fontWeight: "700", fontSize: 13, cursor: "pointer" }}>
                  Activar cobro con Mercado Pago
                </label>
              </div>
              <Row label="Public Key">
                <input style={styles.input} value={form.mpPublicKey} onChange={setF("mpPublicKey")} placeholder="APP_USR-xxxxxxxx..." />
              </Row>
              <Row label="Access Token">
                <input style={styles.input} value={form.mpAccessToken} onChange={setF("mpAccessToken")}
                  placeholder={editItem ? "Vacío = conservar el actual" : "APP_USR-xxxxxxxx..."} />
              </Row>
              <Row label="Webhook Secret (firma de notificaciones)">
                <input style={styles.input} value={form.mpWebhookSecret} onChange={setF("mpWebhookSecret")}
                  placeholder={editItem ? "Vacío = conservar el actual" : "opcional"} />
              </Row>
              <Row label="Modo">
                <select style={styles.input} value={form.mpMode} onChange={setF("mpMode")}>
                  <option value="test">Test (pruebas)</option>
                  <option value="production">Producción (cobros reales)</option>
                </select>
              </Row>
              <Row label="Comisión adicional (%) — opcional">
                <input style={styles.input} type="number" min="0" value={form.mpFeePercent} onChange={setF("mpFeePercent")} placeholder="0" />
              </Row>
            </Section>

            {/* Preview ticket */}
            <Section title="Preview ticket">
              <div style={{
                background: form.colorFondo, borderRadius: 12, padding: 16,
                display: "flex", flexDirection: "column", alignItems: "center", gap: 8
              }}>
                <div style={{
                  background: form.colorTicket, borderRadius: 10, padding: "16px 24px",
                  width: 200, textAlign: "center"
                }}>
                  <div style={{ fontWeight: "800", fontSize: 13, color: form.colorFuente }}>TICKET #1</div>
                  <div style={{ width: 80, height: 80, background: "#fff", margin: "8px auto", borderRadius: 6,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#aaa" }}>
                    QR
                  </div>
                  <div style={{ fontSize: 11, color: form.colorFuente }}>DNI: 12345678</div>
                  <div style={{ fontSize: 11, color: form.colorFuente }}>Juan Pérez</div>
                </div>
              </div>
            </Section>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button
                style={{ ...(editItem ? styles.goldBtn : styles.sininBtn), flex: 1, opacity: saving ? .65 : 1 }}
                onClick={handleGuardar}
                disabled={saving}>
                {saving ? "ESPERE..." : (editItem ? "GUARDAR CAMBIOS" : "CREAR EMPRESA")}
              </button>
              <button style={styles.cancelBtn} onClick={() => { setShowForm(false); setTab("empresas"); }}>
                CANCELAR
              </button>
            </div>
          </div>
        )}

        {/* ── LISTA EMPRESAS ── */}
        {tab === "empresas" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Empresas ({empresas.length})</h2>
              <button style={styles.sininBtn} onClick={abrirNueva}>+ NUEVA EMPRESA</button>
            </div>

            {!loading && globalStats && (
              <div style={styles.statsGrid}>
                {[
                  ["EMPRESAS", globalStats.empresas],
                  ["EVENTOS", globalStats.eventos],
                  ["TICKETS", globalStats.tickets],
                  ["INGRESARON", globalStats.ingresaron],
                  ["PENDIENTES", globalStats.pendientes],
                ].map(([label, value]) => (
                  <div key={label} style={styles.statsCard}>
                    <strong style={styles.statsValue}>{value}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            )}

            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
                <GlassSpinner size={70} background="#171717" />
              </div>
            ) : empresas.length === 0 ? (
              /* Si no cargaron empresas todavía mostramos la de Sensoria hardcodeada como ejemplo */
              <div style={styles.card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <img src={sininLogo} alt="Sensoria" style={styles.companyLogo} />
                    <div>
                      <div style={{ fontWeight: "700", fontSize: 16 }}>Sensoria</div>
                      <div style={{ fontSize: 12, color: "rgba(223,255,251,.66)", marginTop: 2 }}>ID: sensoria</div>
                      <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                        <div style={{ width: 22, height: 22, borderRadius: 4, background: "#dfa66b", border: "1px solid #ccc" }} />
                        <div style={{ width: 22, height: 22, borderRadius: 4, background: "#3a3a3a", border: "1px solid #ccc" }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <button style={styles.goldBtn} onClick={() => entrarComoEmpresa({
                      empresaId: "sensoria", nombre: "Sensoria",
                      colorTicket: "#dfa66b", colorFondo: "#3a3a3a",
                    })}>Ver panel</button>
                    <button style={styles.grayBtn} onClick={() => abrirEditar({
                      empresaId: "sensoria", nombre: "Sensoria",
                      colorTicket: "#dfa66b", colorFondo: "#3a3a3a",
                    })}>Editar</button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {empresas.map(emp => (
                  <div key={emp.empresaId} style={styles.card}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: 0 }}>
                        <img
                          src={companyLogoSrc(emp)}
                          alt={emp.nombre || emp.empresaId}
                          style={styles.companyLogo}
                          onError={e => { e.currentTarget.src = sininLogo; }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: "800", fontSize: 16, color: "#fff" }}>{emp.nombre}</div>
                          <div style={{ fontSize: 12, color: "rgba(223,255,251,.66)", marginTop: 2 }}>ID: {emp.empresaId}</div>
                          <div style={{ fontSize: 12, color: "rgba(223,255,251,.66)", overflowWrap: "anywhere" }}>{emp.smtpUser || "Sin email configurado"}</div>
                          <div style={styles.companyStats}>
                            {emp._stats?.eventos || 0} eventos · {emp._stats?.tickets || 0} tickets · {emp._stats?.ingresaron || 0} ingresaron
                          </div>
                          <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                            <div style={{ width: 22, height: 22, borderRadius: 4, background: emp.colorTicket || "#dfa66b", border: "1px solid #ccc" }} title="Color ticket" />
                            <div style={{ width: 22, height: 22, borderRadius: 4, background: emp.colorFondo || "#3a3a3a", border: "1px solid #ccc" }} title="Color fondo" />
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <button style={styles.goldBtn} onClick={() => entrarComoEmpresa(emp)}>Ver panel</button>
                        <button style={styles.grayBtn} onClick={() => abrirEditar(emp)}>Editar</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <ToastPopup toast={toast} onClose={closeToast} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 11, color: SININ_TURQ, fontWeight: "900", textTransform: "uppercase",
        letterSpacing: 1.2, marginBottom: 9, borderBottom: "1px solid rgba(37,244,223,.25)", paddingBottom: 6 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ marginTop: 10 }}>
      <label style={{ display: "block", fontWeight: "700", fontSize: 12, color: "#dffffb", marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const styles = {
  page:       { background: "radial-gradient(circle at 14% 8%, rgba(37,244,223,.28), transparent 28%), radial-gradient(circle at 88% 78%, rgba(34,106,255,.2), transparent 34%), linear-gradient(145deg, #061315 0%, #05080a 58%, #0b1518 100%)", minHeight: "100vh", color: "#fff", backgroundAttachment: "fixed" },
  header:     { background: "rgba(6,22,24,.68)", color: "#fff", padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(37,244,223,.24)", boxShadow: "0 10px 30px rgba(0,0,0,.28)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)" },
  headerLogo: { width: 120, maxHeight: 54, objectFit: "contain" },
  tabs:       { display: "flex", gap: 8, padding: "8px 14px", background: "rgba(255,255,255,.055)", borderBottom: "1px solid rgba(255,255,255,.1)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" },
  content:    { padding: 18, maxWidth: 700, margin: "0 auto" },
  card:       { background: "linear-gradient(145deg, rgba(255,255,255,.15), rgba(255,255,255,.07))", border: "1px solid rgba(255,255,255,.2)", borderRadius: 24, padding: 19, marginBottom: 12, boxShadow: "inset 0 1px 1px rgba(255,255,255,.16), 0 20px 45px rgba(0,0,0,.3)", backdropFilter: "blur(18px) saturate(135%)", WebkitBackdropFilter: "blur(18px) saturate(135%)" },
  companyTitleRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 14 },
  companyLogo: { width: 60, height: 60, flexShrink: 0, objectFit: "contain",
    boxSizing: "border-box", background: "transparent" },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 10, marginBottom: 18 },
  statsCard: { background: "linear-gradient(145deg, rgba(37,244,223,.2), rgba(255,255,255,.07))",
    border: "1px solid rgba(190,255,248,.24)", borderRadius: 18, padding: "14px 10px", textAlign: "center",
    display: "flex", flexDirection: "column", gap: 3, color: "rgba(234,255,252,.76)", fontSize: 10, fontWeight: 900,
    boxShadow: "inset 0 1px 1px rgba(255,255,255,.17), 0 10px 24px rgba(0,0,0,.2)" },
  statsValue: { color: SININ_TURQ, fontSize: 28, lineHeight: 1 },
  companyStats: { marginTop: 6, color: SININ_TURQ, fontSize: 11, fontWeight: 800 },
  input:      { padding: "11px 14px", borderRadius: 18, border: "1px solid rgba(190,255,248,.24)", fontSize: 13, boxSizing: "border-box", width: "100%", background: "rgba(2,14,16,.58)", color: "#effffd", outlineColor: SININ_TURQ, boxShadow: "inset 0 2px 8px rgba(0,0,0,.22)" },
  goldBtn:    { background: `linear-gradient(145deg, rgba(207,255,250,.9), ${SININ_TURQ} 48%, #0bbbad)`, color: "#042b28", border: "1px solid rgba(224,255,252,.9)", borderRadius: 25, padding: "10px 18px", cursor: "pointer", fontWeight: "900", fontSize: 12, textTransform: "uppercase", boxShadow: "inset 0 2px 1px rgba(255,255,255,.72), inset 0 -5px 10px rgba(0,85,79,.18), 0 5px 0 #087d75, 0 12px 22px rgba(37,244,223,.22)" },
  sininBtn:   { background: `linear-gradient(145deg, rgba(207,255,250,.9), ${SININ_TURQ} 48%, #0bbbad)`,
    color: "#042b28", border: "1px solid rgba(224,255,252,.9)", borderRadius: 25, padding: "10px 19px",
    cursor: "pointer", fontWeight: "900", fontSize: 13, letterSpacing: .45,
    boxShadow: "inset 0 2px 1px rgba(255,255,255,.72), inset 0 -5px 10px rgba(0,85,79,.18), 0 6px 0 #087d75, 0 13px 25px rgba(37,244,223,.25)" },
  grayBtn:    { background: "linear-gradient(145deg, rgba(255,255,255,.16), rgba(255,255,255,.055))", color: "#e8fffc", border: "1px solid rgba(190,255,248,.26)", borderRadius: 25, padding: "9px 15px", cursor: "pointer", fontSize: 12, fontWeight: "800", textTransform: "uppercase", boxShadow: "inset 0 1px 1px rgba(255,255,255,.22), 0 5px 12px rgba(0,0,0,.22)" },
  cancelBtn:  { background: "linear-gradient(145deg, rgba(255,255,255,.18), rgba(5,35,37,.5))", color: "#eafffc", border: "1px solid rgba(190,255,248,.28)", borderRadius: 25, padding: "10px 18px", cursor: "pointer", fontWeight: "800", fontSize: 12, boxShadow: "inset 0 1px 1px rgba(255,255,255,.2), 0 5px 14px rgba(0,0,0,.25)" },
  outlineBtn: { background: "linear-gradient(145deg, rgba(255,255,255,.16), rgba(37,244,223,.08))", color: "#eafffc", border: "1px solid rgba(190,255,248,.4)", borderRadius: 25, padding: "9px 17px", cursor: "pointer", fontSize: 12, fontWeight: "900", boxShadow: "inset 0 1px 1px rgba(255,255,255,.24), 0 5px 14px rgba(0,0,0,.22)" },
  savingOverlay: { position: "fixed", inset: 0, zIndex: 20000, background: "rgba(3,12,14,.82)",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    padding: 24, textAlign: "center", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" },
  savingTitle: { marginTop: 24, color: "#effffd", fontSize: 18, fontWeight: "900", letterSpacing: 1.2 },
  savingText: { marginTop: 8, color: "rgba(239,255,253,.7)", fontSize: 13, maxWidth: 320, lineHeight: 1.5 },
};

const adminTabStyle = active => ({
  flex: 1,
  padding: "11px 10px",
  border: active ? "1px solid rgba(183,255,248,.55)" : "1px solid transparent",
  borderRadius: 25,
  background: active
    ? "linear-gradient(145deg, rgba(207,255,250,.24), rgba(37,244,223,.12))"
    : "transparent",
  color: active ? "#eafffc" : "rgba(234,255,252,.62)",
  boxShadow: active ? "inset 0 1px 1px rgba(255,255,255,.3), 0 7px 18px rgba(37,244,223,.16)" : "none",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: active ? "900" : "700",
  textTransform: "uppercase",
  letterSpacing: .45,
});
